// Models.jsx
import React, { useState, useEffect } from 'react';
import { Button, message, Space, Spin } from 'antd';
import axios from 'axios';
import GenericTable from '../components/GenericTable'; // Import the generic table
import GenericModal from '../components/GenericModal '; // Import the generic modal
import './Models.css'; // Custom CSS for Models page
import AppMenu from '../components/AppMenu';
import { format } from 'date-fns'; 
import GenericViewDrawer from "../components/GenericViewModal";

const Models = () => {
    const [models, setModels] = useState([]);
    const [providers, setProviders] = useState([]); // To store providers
    const [apiKeys, setApiKeys] = useState([]); // To store API keys
    const [loading, setLoading] = useState(true); // For overall page loading state
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [currentModel, setCurrentModel] = useState(null); // For editing a specific model
    const [initialFormData, setInitialFormData] = useState({}); // Track initial form data
    const [selectedType, setSelectedType] = useState(''); // Track selected type

    const [isViewDrawerVisible, setIsViewDrawerVisible] = useState(false);
    const [viewDrawerData, setViewDrawerData] = useState(null);

    const [page, setPage] = useState(1); // Current page
    const [limit] = useState(10); // Items per page
    const [search, setSearch] = useState(""); // Search query
  
    const [total, setTotal] = useState(0);

    useEffect(() => {
        fetchModels();
        fetchProviders(); // Fetch providers for the select dropdown
        fetchApiKeys(); // Fetch API keys for the select dropdown
        localStorage.setItem('lastPath', '/model');
    }, []);


    useEffect(()=>
    {
        fetchModels()
    },[search])

    // Fetch all models
    const fetchModels = async (currentPage) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API}/api/models`, {
                params: {page: currentPage || page, limit, search },            });
            setModels(response.data.data);
            setTotal(response.data.meta.total);
        } catch (error) {
            console.error('Error fetching models:', error);
            message.error('Failed to load models');
        } finally {
            setLoading(false); // Set loading to false once data is fetched
        }
    };

    // Fetch providers for the select dropdown
    const fetchProviders = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API}/api/providers`);
            setProviders(Array.isArray(response.data.data) ? response.data.data : []);
        } catch (error) {
            console.error('Error fetching providers:', error);
            message.error('Failed to load providers');
            setProviders([]);
        }
    };

    // Fetch API keys for the select dropdown
    const fetchApiKeys = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API}/api/apikeys`);
            setApiKeys(response.data);
        } catch (error) {
            console.error('Error fetching API keys:', error);
            message.error('Failed to load API keys');
        }
    };

    // Format models data to display in the table
    const formatModelsData = () => {
        return models.map((model) => {
            return {
                id: model.id, // Model ID
                name: model.name, // Model Name
                version: model.version,
                type: model.type ? model.type : "TEXT", // Add model type
                isActive: model.isActive ? "ACTIVE":"INACTIVE",
                providerName: model.provider.name, // Provider Name
                inputTokenCost: model.Pricing[0] ? (model.Pricing[0].inputTokenCost * 1000000) : 'N/A',           
                outputTokenCost: model.Pricing[0] ? (model.Pricing[0].outputTokenCost * 1000000) : 'N/A', // Calculate output cost per 1000 tokens
                inputSecondCost: model.Pricing[0] ? (model.Pricing[0].inputSecondCost * 1000000) : 'N/A', // Input second cost for STT
                createdAt: format(new Date(model.createdAt), 'MM/dd/yyyy, HH:mm:ss'),
                updatedAt: format(new Date(model.updatedAt), 'MM/dd/yyyy, HH:mm:ss'),
            };
        });
    };


    const handleSearchChange = (value) => {
        console.log("Search Value in Parent:", value);
        setSearch(value);
      
      };
    

    // Compare form data and send only modified fields
    const getModifiedFields = (formData) => {
        const modifiedFields = {};
        Object.keys(formData).forEach(key => {
            if (formData[key] !== initialFormData[key]) {
                modifiedFields[key] = formData[key];
            }
        });
        return modifiedFields;
    };

    // Create or update model
    const handleCreateUpdateModel = async (formData) => {
        try {
            // Clone formData to avoid directly modifying the original object
            const modifiedData = { ...getModifiedFields(formData) };
    
            // Remove providerName and apiKeyName from the payload
            delete modifiedData.providerName;
            delete modifiedData.apiKeyName;
    
            // Convert token costs back to per token if they were updated
            if (modifiedData?.inputTokenCost) {
                modifiedData.inputTokenCost = parseFloat(modifiedData.inputTokenCost) /  1000000;
            }
            if (modifiedData?.outputTokenCost) {
                modifiedData.outputTokenCost = parseFloat(modifiedData.outputTokenCost) / 1000000;
            }
            if (modifiedData?.inputSecondCost) {
                modifiedData.inputSecondCost = parseFloat(modifiedData.inputSecondCost); // Assuming per second cost does not need adjustment
            }
    
            if (currentModel) {
                // Update model
                await axios.patch(`${process.env.REACT_APP_API}/api/models/${currentModel.id}`, modifiedData);
                message.success('Model updated successfully');
            } else {
                // Create new model
                await axios.post(`${process.env.REACT_APP_API}/api/models`, modifiedData);
                message.success('Model created successfully');
            }
            
            fetchModels(); // Refresh the models list
            closeModal(); // Close modal after action
            setCurrentModel(null); // Reset current model state
    
        } catch (error) {
            console.error('Error creating/updating model:', error);
            message.error('Failed to save model');
        }
    };

    // Toggle model status
    const handleToggleModelStatus = async (model) => {
        try {
            await axios.patch(`${process.env.REACT_APP_API}/api/models/${model.id}/activate`,{});
            message.success('Model status updated successfully');
            fetchModels(); // Refresh the models list after update
        } catch (error) {
            console.error('Error updating model status:', error);
            message.error('Failed to update model status');
        }
    };

    const handleViewDetails = (model) => {
       
        setViewDrawerData(model);
        setIsViewDrawerVisible(true);
      };

    // Open modal to create or update model
    const openModal = (model = null) => {
        if (model) {
            // Find the full model data
            const fullModelData = models.find(m => m.id === model.id);
            
            // Map providerName to providerId
            const provider = providers.find(p => p.name === model.providerName);
            
            setCurrentModel(fullModelData);
            setInitialFormData({
                ...fullModelData,
                providerId: provider ? provider.id : undefined,  // If provider found, map its id
            });
            setSelectedType(fullModelData.type); // Set selected type for dynamic form fields
        } else {
            setInitialFormData({});
            setSelectedType(''); // Reset the selected type when creating a new model
        }
        setIsModalVisible(true);
    };

    // Close modal
    const closeModal = () => {
        setIsModalVisible(false); // Close modal
        setCurrentModel(null); // Reset current model
        setSelectedType(''); // Reset selected type
    };

    // Actions for the GenericTable
    const actions = ['Toggle Status', 'View','Edit'];
    const actionFunctions = [
        (model) => handleToggleModelStatus(model),
        (model) => handleViewDetails(model),
        (model) => openModal(model), // Edit action
    ];

    if (loading) {
        return <Spin size="large" style={{ display: 'block', margin: '20px auto' }} />;
    }

    const types = [
        { label: "TEXT", value: "TEXT" },
        { label: "STT", value: "STT" },
        { label: "TTS", value: "TTS" },
        { label: "EMBEDDING", value: "EMBEDDING" }
    ];

    // Conditionally add fields based on selected type
    const dynamicInputs = [];

    if (selectedType === "TEXT" || selectedType === "EMBEDDING" || selectedType === "TTS") {
        dynamicInputs.push({
            label: 'Input Token Cost (per 1M tokens)',
            name: 'inputTokenCost',
            type: 'number',
            placeholder: 'Enter cost for 1M input tokens',
            require: true
        });

        if (selectedType === "TEXT") {
            dynamicInputs.push({
                label: 'Output Token Cost (per 1000 tokens)',
                name: 'outputTokenCost',
                type: 'number',
                placeholder: 'Enter cost for 1000 output tokens',
                require: true
            });
        }
    }

    if (selectedType === "STT") {
        dynamicInputs.push({
            label: 'Input Minute Cost',
            name: 'inputSecondCost',
            type: 'number',
            placeholder: 'Enter cost for each input minute',
        });
    }
    const onPageChange = (pageNumber) => {
        setPage(pageNumber); // Update the current page
        fetchModels(); // Fetch data for the new page
      };

    return (
        <div style={{display:"flex"}}>
            <AppMenu/>
            <div className="models-page">
                <Space style={{width:"98%",display:"flex",justifyContent:"end"}}>
                    <Button type="primary" onClick={() => openModal()}>
                        Create Model
                    </Button>
                </Space>

                {/* Generic Table to show models */}
                <GenericTable
                    data={formatModelsData()} // Pass formatted model data to the table
                    searchKeys={['name', 'providerName']} // Allow search by name and provider
                    sortableColumns={['name', 'providerName', 'type' ]} // Allow sorting on these columns
                    actions={actions} // Pass actions to table
                    actionFunctions={actionFunctions} // Action functions for toggle status and edit
                    onRowClick={openModal} // Enable row click to edit
                    toggle="isActive"
                    pagination={{
                        current: page, // Current page state
                        pageSize: limit, // Items per page
                        total, // Total count from backend
                        onChange: (pageNumber) => {
                          setPage(pageNumber); // Update the page state
                          fetchModels(pageNumber); // Fetch providers for the new page
                        },
                    }}
                    onSearch={handleSearchChange}
                />
                  {/* View Drawer */}
                <GenericViewDrawer
                isVisible={isViewDrawerVisible}
                handleCancel={() => setIsViewDrawerVisible(false)}
                data={viewDrawerData}
                title="model Details"
                footerButtons={["Close"]}
                onButtonClick={() => setIsViewDrawerVisible(false)}
                
                />
                {/* Generic Modal for Create/Edit Model */}
                <GenericModal
                    isVisible={isModalVisible} // Modal visibility state
                    handleOk={(formData) => handleCreateUpdateModel(formData)} // Handle submit action
                    handleCancel={closeModal} // Handle cancel action
                    title={currentModel ? 'Edit Model' : 'Create Model'} // Dynamic title
                    inputs={[
                        { label: 'Name', name: 'name', type: 'text', placeholder: 'Enter model name', require: true },
                        { label: 'Version', name: 'version', type: 'text', placeholder: 'Enter version', require: true },
                        {
                            label: 'Provider',
                            name: 'providerId',
                            type: 'select', // Add a select type for provider
                            options: (providers).map(provider => ({ value: provider.id, label: provider.name })), // Ensure providers is an array
                            require: true
                        },
                        {
                            label: 'Type',
                            name: 'type',
                            type: 'select', // Add a select type for type
                            options: types.map(type => ({ value: type.value, label: type.label })), // Dynamically load type options
                            placeholder: 'Select Type',
                            require: true,
                            onChange: (value) => setSelectedType(value), // Update selected type on change
                        },
                        ...dynamicInputs // Add dynamic inputs based on the selected type
                    ]}
                    actions={['Submit', 'Cancel']} // Actions for modal
                    actionFunctions={[
                        (formData) => handleCreateUpdateModel(formData), // On submit action
                        closeModal, // On cancel action
                    ]}
                    predefinedData={initialFormData} // Pass current model data if editing
                    
                />
            </div>
        </div>
    );
};

export default Models;
